import { useId, useRef, useState } from 'react';
import { useConfirm, usePrompt } from '@/components/DialogProvider';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  templatesQuery,
  templateQuery,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  type TemplateSummary,
  type TemplateResponse,
} from '@/lib/templates';
import { ApiError, messageFromError } from '@/lib/api';
import { posthog } from '@/lib/posthog';
import { meQuery } from '@/lib/me';
import { cn } from '@/lib/utils';
import { usePageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/ToastProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/_app/templates')({
  loader: ({ context }) => context.queryClient.ensureQueryData(meQuery),
  component: TemplatesPage,
});

const CATEGORIES = ['transactional', 'operational', 'marketing'] as const;
type Category = (typeof CATEGORIES)[number];

const CATEGORY_LABELS: Record<Category, string> = {
  transactional: 'Transactional',
  operational: 'Operational',
  marketing: 'Marketing',
};

const CATEGORY_VARIANT: Record<Category, 'info' | 'success' | 'gold'> = {
  transactional: 'info',
  operational: 'success',
  marketing: 'gold',
};

function CategoryBadge({ category }: { category: Category }) {
  return <Badge variant={CATEGORY_VARIANT[category]}>{CATEGORY_LABELS[category]}</Badge>;
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <Badge variant={active ? 'success' : 'neutral'} dot>
      {active ? 'Active' : 'Inactive'}
    </Badge>
  );
}

interface TemplateFormState {
  code: string;
  name: string;
  category: Category;
  langs: string[]; // language codes
  bodies: Record<string, string>; // lang -> body
  placeholders: string;
}

const DEFAULT_FORM: TemplateFormState = {
  code: '',
  name: '',
  category: 'transactional',
  langs: ['en'],
  bodies: { en: '' },
  placeholders: '',
};

function templateToForm(t: TemplateResponse): TemplateFormState {
  const langs = Object.keys(t.body_variants);
  return {
    code: t.code,
    name: t.name,
    category: t.category,
    langs: langs.length > 0 ? langs : ['en'],
    bodies: { ...t.body_variants },
    placeholders: (t.placeholders ?? []).join(', '),
  };
}

interface TemplateModalProps {
  editing?: TemplateResponse | null;
  onClose: () => void;
  canWrite: boolean;
}

function TemplateModal({ editing, onClose, canWrite }: TemplateModalProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<TemplateFormState>(
    editing ? templateToForm(editing) : DEFAULT_FORM,
  );
  const [activeLang, setActiveLang] = useState(form.langs[0] ?? 'en');
  const [error, setError] = useState<string | null>(null);
  const askConfirm = useConfirm();
  const askPrompt = usePrompt();

  const codeId = useId();
  const nameId = useId();
  const catId = useId();
  const phId = useId();

  const createMut = useMutation({
    mutationFn: () =>
      createTemplate({
        throwOnError: true,
        body: {
          code: form.code,
          name: form.name,
          category: form.category,
          body_variants: form.bodies,
          placeholders: form.placeholders.split(',').map((s) => s.trim()).filter(Boolean),
        },
      }),
    onSuccess: () => {
      posthog.capture?.('pms_action', { action: 'template_created' });
      qc.invalidateQueries({ queryKey: ['templates'] });
      toast({ kind: 'success', title: 'Template created', message: form.name });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : String(err));
    },
  });

  const editingId = editing?.id ?? '';

  const updateMut = useMutation({
    mutationFn: () =>
      updateTemplate({
        throwOnError: true,
        path: { id: editingId },
        body: {
          name: form.name,
          category: form.category,
          body_variants: form.bodies,
          placeholders: form.placeholders.split(',').map((s) => s.trim()).filter(Boolean),
        },
      }),
    onSuccess: () => {
      posthog.capture?.('pms_action', { action: 'template_updated', template_id: editingId });
      qc.invalidateQueries({ queryKey: ['templates'] });
      // Also drop the single-template cache so reopening the row within
      // staleTime shows the edit, not the pre-edit snapshot.
      qc.invalidateQueries({ queryKey: ['template', editingId] });
      toast({ kind: 'success', title: 'Template updated', message: form.name });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : String(err));
    },
  });

  const deleteMut = useMutation({
    mutationFn: () =>
      deleteTemplate({
        throwOnError: true,
        path: { id: editingId },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      toast({ kind: 'success', title: 'Template deleted' });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : String(err));
    },
  });

  const isPending = createMut.isPending || updateMut.isPending || deleteMut.isPending;

  async function addLang() {
    const lang = await askPrompt({
      title: 'Add language',
      label: 'Language code (e.g. ru, de, fr)',
      placeholder: 'ru',
      confirmLabel: 'Add',
    });
    if (!lang || form.langs.includes(lang)) return;
    setForm((f) => ({
      ...f,
      langs: [...f.langs, lang],
      bodies: { ...f.bodies, [lang]: '' },
    }));
    setActiveLang(lang);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (editing) {
      updateMut.mutate();
    } else {
      createMut.mutate();
    }
  }

  return (
    <dialog
      ref={dialogRef}
      open
      className="fixed inset-0 z-50 m-auto h-fit max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-0 shadow-[var(--shadow-lg)] backdrop:bg-black/40"
      onClose={onClose}
    >
      <div className="flex items-center justify-between border-b border-[var(--border-soft)] px-6 py-4">
        <h2 className="text-lg font-semibold text-[var(--text-heading)]">
          {editing ? 'Edit Template' : 'New Template'}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-[var(--radius-control)] p-1 text-muted transition hover:bg-[var(--surface-sunken)]"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-4 p-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor={codeId} className="text-sm font-medium">
              Code
            </label>
            <input
              id={codeId}
              type="text"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              disabled={!!editing || !canWrite}
              pattern="[a-z0-9_-]+"
              title="Lowercase letters, digits, hyphen, underscore"
              placeholder="welcome_message"
              className="rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)] disabled:opacity-50"
              required
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={nameId} className="text-sm font-medium">
              Name
            </label>
            <input
              id={nameId}
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              disabled={!canWrite}
              placeholder="Welcome message"
              className="rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)] disabled:opacity-50"
              required
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor={catId} className="text-sm font-medium">
            Category
          </label>
          <select
            id={catId}
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as Category }))}
            disabled={!canWrite}
            className="rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)] disabled:opacity-50"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Body variants</span>
            {canWrite && (
              <button
                type="button"
                onClick={addLang}
                className="text-xs text-primary hover:underline"
              >
                + Add language
              </button>
            )}
          </div>
          <div className="flex gap-1 border-b border-[var(--border-soft)] pb-1">
            {form.langs.map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setActiveLang(lang)}
                className={cn(
                  'rounded-t-[var(--radius-control)] px-3 py-1 text-xs font-medium transition',
                  activeLang === lang
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted hover:bg-[var(--surface-sunken)]',
                )}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
          <textarea
            value={form.bodies[activeLang] ?? ''}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                bodies: { ...f.bodies, [activeLang]: e.target.value },
              }))
            }
            disabled={!canWrite}
            rows={8}
            placeholder={`Body text for ${activeLang}. Use {{guest_name}} placeholders.`}
            className="w-full rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 font-mono text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)] disabled:opacity-50"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor={phId} className="text-sm font-medium">
            Placeholders
            <span className="ml-1 text-xs text-muted">(comma-separated)</span>
          </label>
          <input
            id={phId}
            type="text"
            value={form.placeholders}
            onChange={(e) => setForm((f) => ({ ...f, placeholders: e.target.value }))}
            disabled={!canWrite}
            placeholder="guest_name, checkin_date, property_name"
            className="rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)] disabled:opacity-50"
          />
        </div>

        {error && (
          <p className="rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between pt-2">
          <div>
            {editing && canWrite && (
              <button
                type="button"
                onClick={async () => {
                  const ok = await askConfirm({
                    title: 'Delete this template?',
                    destructive: true,
                    confirmLabel: 'Delete',
                  });
                  if (ok) deleteMut.mutate();
                }}
                disabled={isPending}
                className="text-sm text-destructive hover:underline disabled:opacity-50"
              >
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            {canWrite && (
              <Button type="submit" variant="primary" loading={isPending} disabled={isPending}>
                {editing ? 'Update' : 'Create'}
              </Button>
            )}
          </div>
        </div>
      </form>
    </dialog>
  );
}

function TemplatesPage() {
  const me = Route.useLoaderData();
  const canWrite = me.roles.includes('super_admin') || me.roles.includes('automation_editor');

  const { data, isLoading, isError } = useQuery(templatesQuery());
  const templates = data?.items ?? [];

  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<TemplateResponse | null>(null);

  const qc = useQueryClient();
  const { toast } = useToast();

  // Fetch the full template (list rows are summaries) and open the modal.
  // The previous code fetched via a dynamic import('@/api/generated/sdk.gen')
  // and let a failed getTemplate reject UNHANDLED — so on any error the modal
  // silently never opened. This fetches through the statically-imported
  // templateQuery (fetchQuery reuses the ['template', id] cache, staleTime 60s)
  // and surfaces the error as a toast, so a failure is visible instead of a
  // dead click.
  async function openEdit(summary: TemplateSummary) {
    try {
      const full = await qc.fetchQuery(templateQuery(summary.id));
      setEditing(full);
    } catch (err) {
      toast({ kind: 'error', title: 'Could not open template', message: messageFromError(err) });
    }
  }

  // Title lives in the Topbar (navTitle); publish the live template count as the
  // subtitle and the "New template" primary into the Topbar's actions slot.
  usePageHeader({
    subtitle: data
      ? `${templates.length} template${templates.length === 1 ? '' : 's'}`
      : undefined,
    actions: canWrite ? (
      <Button type="button" variant="primary" size="sm" onClick={() => setShowNew(true)}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        New template
      </Button>
    ) : undefined,
  });

  return (
    <>
      {isLoading && <p className="text-muted">Loading…</p>}
      {isError && <p className="text-sm text-destructive">Failed to load templates.</p>}

      {!isLoading && !isError && (
        <div className="overflow-x-auto overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-sunken)] text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-muted">
                    No templates yet.
                  </td>
                </tr>
              )}
              {templates.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => openEdit(t)}
                  className="cursor-pointer border-b border-[var(--border-soft)] transition last:border-b-0 hover:bg-[var(--surface-sunken)]"
                >
                  <td className="px-4 py-3 font-mono text-xs">{t.code}</td>
                  <td className="px-4 py-3">{t.name}</td>
                  <td className="px-4 py-3">
                    <CategoryBadge category={t.category} />
                  </td>
                  <td className="px-4 py-3">
                    <ActiveBadge active={t.is_active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <TemplateModal
          onClose={() => setShowNew(false)}
          canWrite={canWrite}
        />
      )}
      {editing && (
        <TemplateModal
          editing={editing}
          onClose={() => setEditing(null)}
          canWrite={canWrite}
        />
      )}
    </>
  );
}
